import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Switch } from "./ui/switch";
import { MapPin, CheckCircle2 } from "lucide-react";

const RETRY_MS = 20000;

function useGeoStatus() {
    const [st, setSt] = React.useState(null);
    const load = React.useCallback(() => { api.get("/teacher/geo-status").then(r => setSt(r.data)).catch(() => {}); }, []);
    React.useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => { clearInterval(id); };
    }, [load]);
    return [st, load];
}

/** Teacher: automatically marks present when within range of school during the Sunday check-in window. */
export function GeoCheckIn() {
    const { user } = useAuth();
    const key = `geoCheckin:${user?.id}`;
    const [enabled, setEnabled] = React.useState(() => localStorage.getItem(key) === "1");
    const [st, reload] = useGeoStatus();
    const [msg, setMsg] = React.useState("");
    const lastTry = React.useRef(0);

    const active = enabled && st?.window_open && !st?.checked_in;

    React.useEffect(() => {
        if (!active) { return undefined; }
        if (!navigator.geolocation) { setMsg("This device can't share location."); return undefined; }
        setMsg("Looking for your location…");
        const watchId = navigator.geolocation.watchPosition(async (pos) => {
            if (Date.now() - lastTry.current < RETRY_MS) return;
            lastTry.current = Date.now();
            try {
                const { data } = await api.post("/teacher/geo-checkin", {
                    lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
                });
                toast.success(data.already ? "You're already marked present" : "You're checked in — marked present");
                setMsg("");
                reload();
            } catch (e) {
                setMsg(e.response?.data?.detail || "Check-in failed, retrying…");
            }
        }, (err) => {
            setMsg(err.code === 1 ? "Location is blocked. Allow location for this site in your browser settings." : "Can't get your location yet…");
        }, { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 });
        return () => { navigator.geolocation.clearWatch(watchId); };
    }, [active, reload]);

    function toggle(v) {
        setEnabled(v);
        localStorage.setItem(key, v ? "1" : "0");
        if (v && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(() => {}, () => {}, { timeout: 10000 });
        }
    }

    if (!st) return null;
    return (
        <div className="rounded-xl border border-border bg-white px-4 py-3 text-sm flex items-start gap-3 max-w-md" data-testid="geo-checkin">
            <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">Location check-in</span>
                    <Switch checked={enabled} onCheckedChange={toggle} data-testid="geo-checkin-toggle" />
                </div>
                <div className="text-xs text-brand-stone mt-1" data-testid="geo-checkin-status">
                    {st.checked_in ? (
                        <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Checked in for today</span>
                    ) : !enabled ? (
                        "Turn on and allow location to be marked present automatically when you arrive."
                    ) : st.window_open ? (msg || "Waiting until you're at school…") : (
                        st.is_school_day
                            ? `Check-in opens ${st.checkin_start}–${st.checkin_end} today. Keep this page open when you arrive.`
                            : `Runs on school Sundays, ${st.checkin_start}–${st.checkin_end}. Open this page when you arrive.`
                    )}
                </div>
            </div>
        </div>
    );
}
